import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { Projet, CategorieMarqueur } from '../../types/map';
import { filterResults } from './useResultsFilter';
import { MIN_SEARCH_CHARS } from './searchTypes';

export type SheetPosition = 'closed' | 'half' | 'full';

export interface LivingLabSearch {
  /** Fond de carte (campus) actif — défaut : premier fond résolu. */
  activeBaseMapId: string | undefined;
  setActiveBaseMapId: (id: string | undefined) => void;
  /** Catégories cochées — défaut : TOUTES (SPEC §2.4). */
  checkedCategorieIds: Set<number>;
  setCheckedCategorieIds: (ids: Set<number>) => void;
  toggleCategorie: (id: number) => void;
  /** Mots-clés en cours de saisie (non encore validés). */
  keywords: string;
  setKeywords: (kw: string) => void;
  /** Mots-clés figés au moment de la dernière validation (bouton 🔍 ou Entrée). */
  submittedKeywords: string;
  /** Recherche textuelle active (validée, au-delà du seuil) — pilote loupe/croix. */
  isSearchActive: boolean;
  submitSearch: () => void;
  resetKeywords: () => void;
  /** Position du bottom sheet « Vos résultats » (SPEC §4). */
  sheetPosition: SheetPosition;
  setSheetPosition: (p: SheetPosition) => void;
  /** Résultats filtrés (fond + catégories + mots-clés validés), un par projet. */
  results: Projet[];
}

const allCategoryIds = (categories: CategorieMarqueur[]): Set<number> =>
  new Set(categories.map((c) => c.id).filter((id): id is number => id != null));

/**
 * État de recherche centralisé du frontend Living Labs (SPEC §2.4, W2.2 persistance URL).
 *
 * Source unique de vérité pour le campus actif, les catégories cochées, les
 * mots-clés et la position du bottom sheet. Calcule les `results` via le
 * contrat de filtrage (W0.2) ; le filtre textuel n'est appliqué qu'après
 * validation, à partir des mots-clés figés (`submittedKeywords`) — une
 * modification du champ après validation ne refiltre pas tant qu'une nouvelle
 * recherche n'est pas soumise (§3.6).
 *
 * @param points - Liste des points à filtrer
 * @param categories - Catégories disponibles
 * @param defaultBaseMapId - UUID du fond de carte par défaut
 * @param initialCheckedCategorieIds - Catégories cochées initiales (optionnel, ex. depuis l'URL)
 */
export function useLivingLabSearch(
  projets: Projet[],
  categories: CategorieMarqueur[],
  defaultBaseMapId: string | undefined,
  initialCheckedCategorieIds?: Set<number>
): LivingLabSearch {
  // --- Fond de carte (campus) ---
  const [activeBaseMapId, setActiveBaseMapId] = useState<string | undefined>(defaultBaseMapId);
  useEffect(() => {
    // Sélectionne le fond par défaut dès qu'il est résolu (chargement async).
    if (defaultBaseMapId && activeBaseMapId === undefined) {
      setActiveBaseMapId(defaultBaseMapId);
    }
  }, [defaultBaseMapId, activeBaseMapId]);

  // --- Catégories cochées (défaut : toutes OU selon initialCheckedCategorieIds) ---
  const [checkedCategorieIds, setCheckedCategorieIds] = useState<Set<number>>(() => {
    // Si des catégories initiales sont fournies (ex. par l'URL), les utiliser
    if (initialCheckedCategorieIds && initialCheckedCategorieIds.size > 0) {
      return new Set(initialCheckedCategorieIds);
    }
    // Sinon, sélectionner toutes les catégories
    return allCategoryIds(categories);
  });

  const initializedRef = useRef(categories.length > 0);
  useEffect(() => {
    if (!initializedRef.current && categories.length > 0) {
      initializedRef.current = true;
      // Si des catégories initiales ont été fournies, les conserver ; sinon, charger toutes
      if (!(initialCheckedCategorieIds && initialCheckedCategorieIds.size > 0)) {
        setCheckedCategorieIds(allCategoryIds(categories));
      }
    }
  }, [categories, initialCheckedCategorieIds]);

  const toggleCategorie = useCallback((id: number) => {
    setCheckedCategorieIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // --- Recherche textuelle ---
  const [keywords, setKeywords] = useState('');
  // Mots-clés figés au moment de la validation ; seuls eux pilotent le filtre
  // texte et l'état loupe/croix (une frappe après validation ne refiltre pas).
  const [submittedKeywords, setSubmittedKeywords] = useState('');
  const submitSearch = useCallback(() => setSubmittedKeywords(keywords), [keywords]);
  const resetKeywords = useCallback(() => {
    setKeywords('');
    setSubmittedKeywords('');
  }, []);
  const isSearchActive = useMemo(
    () => submittedKeywords.trim().length >= MIN_SEARCH_CHARS,
    [submittedKeywords]
  );

  // --- Bottom sheet ---
  const [sheetPosition, setSheetPosition] = useState<SheetPosition>('closed');

  // --- Résultats filtrés ---
  const results = useMemo(
    () =>
      filterResults(projets, {
        baseMapId: activeBaseMapId,
        checkedCategorieIds,
        keywords: submittedKeywords,
      }),
    [projets, activeBaseMapId, checkedCategorieIds, submittedKeywords]
  );

  return {
    activeBaseMapId,
    setActiveBaseMapId,
    checkedCategorieIds,
    setCheckedCategorieIds,
    toggleCategorie,
    keywords,
    setKeywords,
    submittedKeywords,
    isSearchActive,
    submitSearch,
    resetKeywords,
    sheetPosition,
    setSheetPosition,
    results,
  };
}
